import React, { useContext, useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { AppContext } from "../App"
import { saveTranslations, loadTranslations } from "../components/translations"
import {
  flattenTranslations,
  unflattenTranslations,
} from "../utils/translationUtils"
import { useLocation } from "react-router-dom"
import { routesMap } from "../routesMap"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import ReviewsManager from "./ReviewsManager"
import HoursEditor from "./HoursEditor"
import EventsManager from "./EventsManager"
import SpecialMenuEditor from "./SpecialMenuEditor"

const TranslationEditor = () => {
  const { i18n, ready } = useTranslation("global")
  const { isEditMode, setIsEditMode } = useContext(AppContext)
  const location = useLocation()

  // Translation states
  const [translations, setTranslations] = useState({})
  const [otherTranslations, setOtherTranslations] = useState({})
  const [flatTranslations, setFlatTranslations] = useState({})
  const [otherFlatTranslations, setOtherFlatTranslations] = useState({})
  const [initialFlatTranslations, setInitialFlatTranslations] = useState({})
  const [initialOtherFlatTranslations, setInitialOtherFlatTranslations] =
    useState({})
  const [initialMenuTranslations, setInitialMenuTranslations] = useState(null)
  const [initialOtherMenuTranslations, setInitialOtherMenuTranslations] =
    useState(null)

  // UI states
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [toggleOtherKeys, setToggleOtherKeys] = useState({})
  const [menuItemToggleOther, setMenuItemToggleOther] = useState({})
  const [sectionToggleOther, setSectionToggleOther] = useState({})
  const [collapsedItems, setCollapsedItems] = useState({})
  const [collapsedSections, setCollapsedSections] = useState({})
  const [isMobile, setIsMobile] = useState(false)
  const [showSidebar, setShowSidebar] = useState(true)
  const [loading, setLoading] = useState(true)

  // New menu item/section modal state
  const [showAddItem, setShowAddItem] = useState(false)
  const [showAddSection, setShowAddSection] = useState(false)
  const [selectedSection, setSelectedSection] = useState("")
  const [newItem, setNewItem] = useState({
    key: "",
    name: "",
    desc: "",
    price: "",
    allergens: [],
  })
  const [newSection, setNewSection] = useState({
    key: "",
    title: "",
  })
  const [allergens, setAllergens] = useState({})

  // Sections navigation
  const [sections, setSections] = useState([])
  const [activeSection, setActiveSection] = useState(null)
  const [showReviewsManager, setShowReviewsManager] = useState(false)
  const [showHoursEditor, setShowHoursEditor] = useState(false)
  const [showEventsManager, setShowEventsManager] = useState(false)
  const [showSpecialMenuEditor, setShowSpecialMenuEditor] = useState(false)
  const [showBackupManager, setShowBackupManager] = useState(false)
  const [backups, setBackups] = useState({ it: [], en: [] })
  const [backupsLoading, setBackupsLoading] = useState(false)
  const [restoringBackup, setRestoringBackup] = useState(null)

  // Confirmation modal states
  const [confirmMissing, setConfirmMissing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [confirmDeleteSection, setConfirmDeleteSection] = useState(null)

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768)
      setShowSidebar(window.innerWidth >= 768)
    }

    checkScreenSize()
    window.addEventListener("resize", checkScreenSize)
    return () => window.removeEventListener("resize", checkScreenSize)
  }, [])

  // Helper: Get current page key from the URL
  const getCurrentPage = useCallback(() => {
    const path = location.pathname.toLowerCase()
    const pathToKey = {
      "/menu-carta": "menuCarta",
      "/wine-list": "wineList",
      "/aperitivo-menu": "menuAperitivo",
      "/gift-card": "giftCard",
      "/about": "aboutUs",
      "/events": "events",
      "/reviews": "reviews",
      "/privacy": "privacy",
      "/": "home",
      "/contacts": "contact",
    }

    for (const [basePath, localizedPaths] of Object.entries(routesMap)) {
      if (localizedPaths.it === path || localizedPaths.en === path) {
        return pathToKey[basePath] || basePath.replace("/", "")
      }
    }
    return "home"
  }, [location.pathname])

  const handleSectionChange = (sectionName) => {
    setActiveSection(sectionName)
    if (isMobile) setShowSidebar(false)

    // Update URL to match the section (this is important to get the correct getCurrentPage() value)
    const sectionToRoute = {
      menuCarta: "/menu-carta",
      wineList: "/wine-list",
      menuAperitivo: "/aperitivo-menu",
      giftCard: "/gift-card",
      aboutUs: "/about",
      events: "/events",
      reviews: "/reviews",
      privacy: "/privacy",
      home: "/",
      contact: "/contacts",
    }

    // Find the route for this section
    const route = sectionToRoute[sectionName]
    if (route) {
      // Simulate navigation to this route (without actual navigation)
      const localizedPath = routesMap[route][i18n.language] || route
      window.history.replaceState(
        null,
        "",
        localizedPath + window.location.search
      )
    }

    // Force reload all data for this section
    const currentLang = i18n.language
    const secondaryLang = getSecondaryLanguage()

    try {
      // Get current language translations
      let currentTranslations =
        i18n.getResourceBundle(currentLang, "global") || {}

      // Get secondary language translations
      let secondaryTranslations =
        i18n.getResourceBundle(secondaryLang, "global") || {}

      // Update translations state
      setTranslations({ ...currentTranslations })
      setOtherTranslations({ ...secondaryTranslations })

      // Update flat translations - include special menu data
      const flatCurr = flattenTranslations(currentTranslations)
      const flatOther = flattenTranslations(secondaryTranslations)
      setFlatTranslations(flatCurr)
      setOtherFlatTranslations(flatOther)
      setInitialFlatTranslations(flatCurr)
      setInitialOtherFlatTranslations(flatOther)

      // Check if new section is a menu page and update menu-specific state
      const isMenuPage = ["menuCarta", "wineList", "menuAperitivo"].includes(
        sectionName
      )
      if (isMenuPage && currentTranslations[sectionName]?.sections) {
        const sections = currentTranslations[sectionName].sections

        // Initialize section and item collapse states
        const initCollapsedSections = {}
        const initCollapsedItems = {}

        Object.keys(sections).forEach((sectionKey) => {
          initCollapsedSections[sectionKey] = true
          const items = sections[sectionKey].items || {}
          Object.keys(items).forEach((itemKey) => {
            initCollapsedItems[`${sectionKey}-${itemKey}`] = true
          })
        })

        setCollapsedSections(initCollapsedSections)
        setCollapsedItems(initCollapsedItems)
        setInitialMenuTranslations(JSON.parse(JSON.stringify(sections)))

        if (secondaryTranslations[sectionName]?.sections) {
          setInitialOtherMenuTranslations(
            JSON.parse(
              JSON.stringify(secondaryTranslations[sectionName].sections)
            )
          )
        }
      }

      // Get allergens for menu pages
      if (currentTranslations?.menuCarta?.allergens?.items) {
        setAllergens(currentTranslations.menuCarta.allergens.items)
      }
    } catch (error) {
      console.error("Error loading section data:", error)
      setMessage({ type: "error", text: "Failed to load section data" })
    }
  }

  // Helper: Returns the secondary language (assumes only "it" and "en")
  const getSecondaryLanguage = () => {
    return i18n.language === "en" ? "it" : "en"
  }

  // Load translations for both languages
  useEffect(() => {
    if (!ready) return

    const fetchTranslations = async () => {
      setLoading(true)
      const currentLang = i18n.language
      const secondaryLang = getSecondaryLanguage()

      try {
        // Get current language translations
        let currentTranslations =
          i18n.getResourceBundle(currentLang, "global") || {}
        // Try to load the most recent translations from server
        try {
          const freshCurrentData = await loadTranslations(currentLang)
          currentTranslations = freshCurrentData
          i18n.addResourceBundle(
            currentLang,
            "global",
            freshCurrentData,
            true,
            true
          )
        } catch (e) {
          console.warn(
            "Could not fetch fresh translations, using local version",
            e
          )
        }

        // Get secondary language translations
        let secondaryTranslations =
          i18n.getResourceBundle(secondaryLang, "global") || {}
        try {
          const freshSecondaryData = await loadTranslations(secondaryLang)
          secondaryTranslations = freshSecondaryData
          i18n.addResourceBundle(
            secondaryLang,
            "global",
            freshSecondaryData,
            true,
            true
          )
        } catch (e) {
          console.warn("Could not fetch secondary language translations", e)
          // If the secondary bundle is still empty, try a direct API call
          if (Object.keys(secondaryTranslations).length === 0) {
            try {
              const response = await fetch(
                `https://www.casadipinta.com/api.php?lang=${secondaryLang}`
              )
              if (response.ok) {
                secondaryTranslations = (await response.json()) || {}
              }
            } catch (err) {
              console.error("Error loading secondary translations:", err)
            }
          }
        }

        // Set up translation states
        setTranslations(currentTranslations)
        setOtherTranslations(secondaryTranslations)

        const flatCurr = flattenTranslations(currentTranslations)
        const flatOther = flattenTranslations(secondaryTranslations)

        setFlatTranslations(flatCurr)
        setOtherFlatTranslations(flatOther)
        setInitialFlatTranslations(flatCurr)
        setInitialOtherFlatTranslations(flatOther)

        // Get allergens for menu pages
        if (currentTranslations?.menuCarta?.allergens?.items) {
          setAllergens(currentTranslations.menuCarta.allergens.items)
        }

        // For menu pages, save initial state of nested sections
        const currentPage = getCurrentPage()
        if (currentTranslations[currentPage]?.sections) {
          const sections = currentTranslations[currentPage].sections

          // Initialize collapsed states for sections and items
          const initCollapsedSections = {}
          const initCollapsedItems = {}

          Object.keys(sections).forEach((sectionKey) => {
            initCollapsedSections[sectionKey] = true
            const items = sections[sectionKey].items || {}
            Object.keys(items).forEach((itemKey) => {
              initCollapsedItems[`${sectionKey}-${itemKey}`] = true
            })
          })

          setCollapsedSections(initCollapsedSections)
          setCollapsedItems(initCollapsedItems)
          setInitialMenuTranslations(JSON.parse(JSON.stringify(sections)))
        }

        if (secondaryTranslations[currentPage]?.sections) {
          setInitialOtherMenuTranslations(
            JSON.parse(
              JSON.stringify(secondaryTranslations[currentPage].sections)
            )
          )
        }

        // Organize translations into sections for navigation
        organizeSections(flatCurr, currentPage)
      } catch (error) {
        console.error("Error loading translations:", error)
        setMessage({ type: "error", text: "Failed to load translations" })
      } finally {
        setLoading(false)
      }
    }

    fetchTranslations()
  }, [i18n, ready, getCurrentPage])

  // Organize translations into sections for navigation
  const organizeSections = (flatTranslations, currentPage) => {
    if (Object.keys(flatTranslations).length === 0) return

    // First identify all top-level sections
    const sectionMap = {}

    Object.keys(flatTranslations).forEach((key) => {
      const section = key.split(".")[0]
      if (!sectionMap[section]) {
        sectionMap[section] = []
      }
      sectionMap[section].push(key)
    })

    // Create an array of sections
    const sectionArray = Object.entries(sectionMap).map(([name, keys]) => ({
      name,
      keys,
    }))

    setSections(sectionArray)

    // Set active section based on current page or default to first section
    const pageSectionName = currentPage === "aboutUs" ? "about" : currentPage
    const matchingSection = sectionArray.find(
      (section) => section.name === pageSectionName
    )

    if (matchingSection) {
      setActiveSection(matchingSection.name)
    } else if (sectionArray.length > 0) {
      setActiveSection(sectionArray[0].name)
    }
  }

  // --- REGULAR (Non-menu) EDITOR FUNCTIONS ---

  const toggleOtherKey = (key) => {
    setToggleOtherKeys((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleRegularChange = (key, value) => {
    setFlatTranslations((prev) => ({ ...prev, [key]: value }))
  }

  const handleOtherRegularChange = (key, value) => {
    setOtherFlatTranslations((prev) => ({ ...prev, [key]: value }))
  }

  // --- MENU EDITOR FUNCTIONS ---

  const handleDragEnd = (result) => {
    if (!result.destination) return
    const { source, destination, draggableId, type } = result
    const currentPage = getCurrentPage()

    // Handle section reordering
    if (type === "section") {
      const sectionKey = draggableId.replace("section-", "")
      const currentSections = { ...translations[currentPage].sections }

      // Create an array of section entries to reorder
      const sectionsArray = Object.entries(currentSections)
      const [movedSection] = sectionsArray.splice(source.index, 1)
      sectionsArray.splice(destination.index, 0, movedSection)

      // Convert the array back to an object with the new order
      const reorderedSections = {}
      sectionsArray.forEach(([key, value]) => {
        reorderedSections[key] = value
      })

      // Update current language
      setTranslations((prev) => ({
        ...prev,
        [currentPage]: {
          ...prev[currentPage],
          sections: reorderedSections,
        },
      }))

      // Update other language to match the order
      const otherSections = otherTranslations[currentPage]?.sections || {}
      const reorderedOtherSections = {}

      sectionsArray.forEach(([key, _]) => {
        if (otherSections[key]) {
          reorderedOtherSections[key] = otherSections[key]
        } else {
          reorderedOtherSections[key] = { title: "", items: {} }
        }
      })

      setOtherTranslations((prev) => ({
        ...prev,
        [currentPage]: {
          ...(prev[currentPage] || {}),
          sections: reorderedOtherSections,
        },
      }))

      return
    }

    // Handle item reordering
    if (type === "item" || !type) {
      const section = draggableId.split("-")[0]
      const currentItems = {
        ...translations[currentPage].sections[section].items,
      }

      // Reorder items
      const itemsArray = Object.entries(currentItems)
      const [movedItem] = itemsArray.splice(source.index, 1)
      itemsArray.splice(destination.index, 0, movedItem)

      const reorderedItems = {}
      itemsArray.forEach(([key, value]) => {
        reorderedItems[key] = value
      })

      // Update current language version
      setTranslations((prev) => ({
        ...prev,
        [currentPage]: {
          ...prev[currentPage],
          sections: {
            ...prev[currentPage].sections,
            [section]: {
              ...prev[currentPage].sections[section],
              items: reorderedItems,
            },
          },
        },
      }))

      // For secondary language, preserve any existing modifications
      const currentOtherItems =
        (otherTranslations[currentPage] &&
          otherTranslations[currentPage].sections &&
          otherTranslations[currentPage].sections[section] &&
          otherTranslations[currentPage].sections[section].items) ||
        {}

      const reorderedOtherItems = {}
      itemsArray.forEach(([key, _]) => {
        reorderedOtherItems[key] =
          currentOtherItems[key] ||
          translations[currentPage].sections[section].items[key]
      })

      setOtherTranslations((prev) => ({
        ...prev,
        [currentPage]: {
          ...(prev[currentPage] || {}),
          sections: {
            ...((prev[currentPage] && prev[currentPage].sections) || {}),
            [section]: {
              ...((prev[currentPage] &&
                prev[currentPage].sections &&
                prev[currentPage].sections[section]) ||
                {}),
              items: reorderedOtherItems,
            },
          },
        },
      }))
    }
  }

  const toggleItemCollapse = (sectionId, itemId) => {
    const key = `${sectionId}-${itemId}`
    setCollapsedItems((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const handleMenuItemChange = (section, itemKey, field, value) => {
    const currentPage = getCurrentPage()
    setTranslations((prev) => ({
      ...prev,
      [currentPage]: {
        ...prev[currentPage],
        sections: {
          ...prev[currentPage].sections,
          [section]: {
            ...prev[currentPage].sections[section],
            items: {
              ...prev[currentPage].sections[section].items,
              [itemKey]: {
                ...prev[currentPage].sections[section].items[itemKey],
                [field]: value,
              },
            },
          },
        },
      },
    }))
  }

  const handleMenuItemOtherChange = (section, itemKey, field, value) => {
    const currentPage = getCurrentPage()
    setOtherTranslations((prev) => ({
      ...prev,
      [currentPage]: {
        ...(prev[currentPage] || {}),
        sections: {
          ...((prev[currentPage] && prev[currentPage].sections) || {}),
          [section]: {
            ...((prev[currentPage] &&
              prev[currentPage].sections &&
              prev[currentPage].sections[section]) ||
              {}),
            items: {
              ...((prev[currentPage] &&
                prev[currentPage].sections &&
                prev[currentPage].sections[section] &&
                prev[currentPage].sections[section].items) ||
                {}),
              [itemKey]: {
                ...((prev[currentPage] &&
                  prev[currentPage].sections &&
                  prev[currentPage].sections[section] &&
                  prev[currentPage].sections[section].items &&
                  prev[currentPage].sections[section].items[itemKey]) ||
                  {}),
                [field]: value,
              },
            },
          },
        },
      },
    }))
  }

  const handleAllergenToggle = (section, itemKey, allergenId) => {
    const currentPage = getCurrentPage()
    setTranslations((prev) => {
      const items = prev[currentPage].sections[section].items
      const currentAllergens = items[itemKey].allergens || []
      const newAllergens = currentAllergens.includes(Number(allergenId))
        ? currentAllergens.filter((id) => id !== Number(allergenId))
        : [...currentAllergens, Number(allergenId)].sort((a, b) => a - b)
      return {
        ...prev,
        [currentPage]: {
          ...prev[currentPage],
          sections: {
            ...prev[currentPage].sections,
            [section]: {
              ...prev[currentPage].sections[section],
              items: {
                ...items,
                [itemKey]: {
                  ...items[itemKey],
                  allergens: newAllergens,
                },
              },
            },
          },
        },
      }
    })
  }

  const handleOtherAllergenToggle = (section, itemKey, allergenId) => {
    const currentPage = getCurrentPage()
    setOtherTranslations((prev) => {
      const items =
        (prev[currentPage] &&
          prev[currentPage].sections &&
          prev[currentPage].sections[section] &&
          prev[currentPage].sections[section].items) ||
        {}
      const currentAllergens = items[itemKey]?.allergens || []
      const newAllergens = currentAllergens.includes(Number(allergenId))
        ? currentAllergens.filter((id) => id !== Number(allergenId))
        : [...currentAllergens, Number(allergenId)].sort((a, b) => a - b)
      return {
        ...prev,
        [currentPage]: {
          ...(prev[currentPage] || {}),
          sections: {
            ...((prev[currentPage] && prev[currentPage].sections) || {}),
            [section]: {
              ...((prev[currentPage] &&
                prev[currentPage].sections &&
                prev[currentPage].sections[section]) ||
                {}),
              items: {
                ...((prev[currentPage] &&
                  prev[currentPage].sections &&
                  prev[currentPage].sections[section] &&
                  prev[currentPage].sections[section].items) ||
                  {}),
                [itemKey]: {
                  ...((prev[currentPage] &&
                    prev[currentPage].sections &&
                    prev[currentPage].sections[section] &&
                    prev[currentPage].sections[section].items &&
                    prev[currentPage].sections[section].items[itemKey]) ||
                    {}),
                  allergens: newAllergens,
                },
              },
            },
          },
        },
      }
    })
  }

  const toggleMenuItemOther = (section, itemKey) => {
    const key = `${section}-${itemKey}`
    setMenuItemToggleOther((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleRemoveItem = (section, itemKey) => {
    setConfirmDelete({ section, itemKey })
  }

  const confirmDeleteAction = (deleteBoth) => {
    const { section, itemKey } = confirmDelete
    const currentPage = getCurrentPage()

    // Remove from current language
    setTranslations((prev) => {
      const newSections = { ...prev[currentPage].sections }
      const newItems = { ...newSections[section].items }
      delete newItems[itemKey]
      newSections[section] = { ...newSections[section], items: newItems }
      return {
        ...prev,
        [currentPage]: { ...prev[currentPage], sections: newSections },
      }
    })

    // Remove from secondary language if requested
    if (deleteBoth) {
      setOtherTranslations((prev) => {
        if (!prev[currentPage]?.sections?.[section]?.items) return prev

        const newSections = { ...(prev[currentPage].sections || {}) }
        const newItems = { ...(newSections[section].items || {}) }
        delete newItems[itemKey]
        newSections[section] = { ...newSections[section], items: newItems }
        return {
          ...prev,
          [currentPage]: {
            ...(prev[currentPage] || {}),
            sections: newSections,
          },
        }
      })
    }
    setConfirmDelete(null)
  }

  const handleAddSection = () => {
    setNewSection({ key: "", title: "" })
    setShowAddSection(true)
  }

  const handleSaveNewSection = () => {
    if (!newSection.key || !newSection.title) {
      setMessage({
        type: "error",
        text: "Both key and title are required for a new section",
      })
      return
    }

    const keyRegex = /^[a-z]+$/
    if (!keyRegex.test(newSection.key)) {
      setMessage({
        type: "error",
        text: "The section key must contain only lowercase letters, no spaces or special characters",
      })
      return
    }

    const currentPage = getCurrentPage()

    // Check if section already exists
    if (translations[currentPage]?.sections?.[newSection.key]) {
      setMessage({
        type: "error",
        text: "This section key already exists. Please choose a different one.",
      })
      return
    }

    // Add new section to current language
    setTranslations((prev) => ({
      ...prev,
      [currentPage]: {
        ...prev[currentPage],
        sections: {
          ...prev[currentPage].sections,
          [newSection.key]: {
            title: newSection.title,
            items: {},
          },
        },
      },
    }))

    // Add new section to other language with same title
    setOtherTranslations((prev) => ({
      ...prev,
      [currentPage]: {
        ...(prev[currentPage] || {}),
        sections: {
          ...((prev[currentPage] && prev[currentPage].sections) || {}),
          [newSection.key]: {
            title: newSection.title,
            items: {},
          },
        },
      },
    }))

    // Update collapsed state for new section
    setCollapsedSections((prev) => ({
      ...prev,
      [newSection.key]: true,
    }))

    setShowAddSection(false)
    setNewSection({ key: "", title: "" })
  }

  const handleRemoveSection = (sectionKey) => {
    setConfirmDeleteSection(sectionKey)
  }

  const confirmDeleteSectionAction = (deleteBoth) => {
    const sectionKey = confirmDeleteSection
    const currentPage = getCurrentPage()

    // Remove from current language
    setTranslations((prev) => {
      const newSections = { ...prev[currentPage].sections }
      delete newSections[sectionKey]
      return {
        ...prev,
        [currentPage]: { ...prev[currentPage], sections: newSections },
      }
    })

    // Remove from secondary language if requested
    if (deleteBoth) {
      setOtherTranslations((prev) => {
        if (!prev[currentPage]?.sections) return prev

        const newSections = { ...prev[currentPage].sections }
        delete newSections[sectionKey]
        return {
          ...prev,
          [currentPage]: {
            ...prev[currentPage],
            sections: newSections,
          },
        }
      })
    }

    setConfirmDeleteSection(null)
  }

  const handleAddItem = (section) => {
    setSelectedSection(section)
    setNewItem({
      key: "",
      name: "",
      desc: "",
      price: "",
      allergens: [],
    })
    setShowAddItem(true)
  }

  const handleSaveNewItem = () => {
    if (!selectedSection || !newItem.key || !newItem.name) {
      setMessage({
        type: "error",
        text: "Please fill in at least the key and name fields",
      })
      return
    }

    const keyRegex = /^[a-z]+$/
    if (!keyRegex.test(newItem.key)) {
      setMessage({
        type: "error",
        text: "The key must contain only lowercase letters, no spaces or special characters",
      })
      return
    }

    const currentPage = getCurrentPage()

    // Update current language
    setTranslations((prev) => {
      const currentItems = prev[currentPage].sections[selectedSection].items
      if (currentItems[newItem.key]) {
        setMessage({
          type: "error",
          text: "This key already exists. Please choose a different one.",
        })
        return prev
      }

      return {
        ...prev,
        [currentPage]: {
          ...prev[currentPage],
          sections: {
            ...prev[currentPage].sections,
            [selectedSection]: {
              ...prev[currentPage].sections[selectedSection],
              items: {
                ...currentItems,
                [newItem.key]: {
                  name: newItem.name,
                  desc: newItem.desc,
                  price: newItem.price,
                  allergens: newItem.allergens,
                },
              },
            },
          },
        },
      }
    })

    // Copy same values to secondary language
    setOtherTranslations((prev) => {
      const currentItems =
        (prev[currentPage] &&
          prev[currentPage].sections &&
          prev[currentPage].sections[selectedSection] &&
          prev[currentPage].sections[selectedSection].items) ||
        {}

      return {
        ...prev,
        [currentPage]: {
          ...(prev[currentPage] || {}),
          sections: {
            ...((prev[currentPage] && prev[currentPage].sections) || {}),
            [selectedSection]: {
              ...((prev[currentPage] &&
                prev[currentPage].sections &&
                prev[currentPage].sections[selectedSection]) ||
                {}),
              items: {
                ...currentItems,
                [newItem.key]: {
                  name: newItem.name,
                  desc: newItem.desc,
                  price: newItem.price,
                  allergens: newItem.allergens,
                },
              },
            },
          },
        },
      }
    })

    setShowAddItem(false)
    setNewItem({ key: "", name: "", desc: "", price: "", allergens: [] })
  }

  // SAVE FUNCTION
  const handleSave = async () => {
    setIsSaving(true)
    setMessage({ type: "loading", text: "Checking translations..." })

    const currentPage = getCurrentPage()
    const isMenuPage = ["menuCarta", "wineList", "menuAperitivo"].includes(
      currentPage
    )

    if (isMenuPage) {
      // Check for menu item fields that were edited in current language
      // but the corresponding other language field was not updated.
      let editedMenuFields = []
      if (initialMenuTranslations && initialOtherMenuTranslations) {
        const currentMenu = translations[currentPage].sections
        const initialMenu = initialMenuTranslations
        const currentOtherMenu = otherTranslations[currentPage]?.sections || {}
        const initialOtherMenu = initialOtherMenuTranslations || {}

        for (const section in currentMenu) {
          for (const itemKey in currentMenu[section].items) {
            const currentItem = currentMenu[section].items[itemKey]
            const initialItem = initialMenu[section]?.items[itemKey] || {}
            const currentOtherItem =
              currentOtherMenu[section]?.items[itemKey] || {}
            const initialOtherItem =
              initialOtherMenu[section]?.items[itemKey] || {}

            for (const field in currentItem) {
              if (
                JSON.stringify(currentItem[field]) !==
                  JSON.stringify(initialItem[field]) &&
                (currentOtherItem[field] === undefined ||
                  JSON.stringify(currentOtherItem[field]) ===
                    JSON.stringify(initialOtherItem[field]))
              ) {
                editedMenuFields.push(`${section}.${itemKey}.${field}`)
              }
            }
          }
        }

        // Also check page text strings for menu pages (including special menu)
        const editedKeys = Object.keys(flatTranslations).filter(
          (key) =>
            key.startsWith(`${currentPage}.`) &&
            !key.includes(".sections.") &&
            flatTranslations[key] !== initialFlatTranslations[key]
        )

        const missingOtherKeys = editedKeys.filter(
          (key) =>
            !otherFlatTranslations[key] ||
            otherFlatTranslations[key] === "" ||
            otherFlatTranslations[key] === initialOtherFlatTranslations[key]
        )

        if (missingOtherKeys.length > 0) {
          editedMenuFields = [...editedMenuFields, ...missingOtherKeys]
        }
      }

      if (editedMenuFields.length > 0 && !confirmMissing) {
        setConfirmMissing({ edited: editedMenuFields })
        setIsSaving(false)
        return
      }
    } else {
      // Regular pages check
      const editedKeys = Object.keys(flatTranslations).filter(
        (key) => flatTranslations[key] !== initialFlatTranslations[key]
      )

      const missingOtherKeys = editedKeys.filter(
        (key) =>
          !otherFlatTranslations[key] ||
          otherFlatTranslations[key] === "" ||
          otherFlatTranslations[key] === initialOtherFlatTranslations[key]
      )

      if (missingOtherKeys.length > 0 && !confirmMissing) {
        setConfirmMissing({ edited: editedKeys, missing: missingOtherKeys })
        setIsSaving(false)
        return
      }
    }

    setMessage({ type: "loading", text: "Saving translations..." })

    try {
      let dataToSaveCurrent, dataToSaveOther

      if (isMenuPage) {
        // Create deep copies to avoid modifying original state
        dataToSaveCurrent = JSON.parse(JSON.stringify(translations))
        dataToSaveOther = JSON.parse(JSON.stringify(otherTranslations))

        // For regular strings on menu pages, manually merge from flatTranslations
        Object.keys(flatTranslations).forEach((key) => {
          if (
            key.startsWith(`${currentPage}.`) &&
            !key.includes(".sections.")
          ) {
            // Use a helper function to set nested property
            setNestedProperty(dataToSaveCurrent, key, flatTranslations[key])
            setNestedProperty(dataToSaveOther, key, otherFlatTranslations[key])
          }
        })
      } else {
        // For regular pages, just use the flattened translations
        dataToSaveCurrent = unflattenTranslations(flatTranslations)
        dataToSaveOther = unflattenTranslations(otherFlatTranslations)
      }

      const resultCurrent = await saveTranslations(
        i18n.language,
        dataToSaveCurrent
      )

      const secondaryLang = getSecondaryLanguage()
      const resultOther = await saveTranslations(secondaryLang, dataToSaveOther)

      // Update i18n resources
      i18n.addResourceBundle(
        i18n.language,
        "global",
        dataToSaveCurrent,
        true,
        true
      )
      i18n.addResourceBundle(
        secondaryLang,
        "global",
        dataToSaveOther,
        true,
        true
      )

      setMessage({
        type: "success",
        text: `Translations saved successfully for both languages!`,
      })

      // Close editor and refresh page
      setTimeout(() => {
        closeEditor()
        window.location.reload()
      }, 1500)
    } catch (error) {
      console.error("Save error:", error)
      setMessage({
        type: "error",
        text: error.message || "Error saving translations",
      })
    } finally {
      setIsSaving(false)
      setConfirmMissing(null)
    }
  }

  // Helper function to set a nested property using a dot-notation string
  const setNestedProperty = (obj, path, value) => {
    const parts = path.split(".")
    let current = obj

    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {}
      }
      current = current[parts[i]]
    }

    current[parts[parts.length - 1]] = value
  }

  const closeEditor = () => {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    )
    setIsEditMode(false)
  }

  // --- BACKUP FUNCTIONS ---

  const loadBackups = async () => {
    setBackupsLoading(true)
    try {
      const [itRes, enRes] = await Promise.all([
        fetch("https://www.casadipinta.com/backup-api.php?lang=it"),
        fetch("https://www.casadipinta.com/backup-api.php?lang=en"),
      ])
      const itData = await itRes.json()
      const enData = await enRes.json()
      setBackups({ it: itData.backups || [], en: enData.backups || [] })
    } catch (error) {
      console.error("Failed to load backups:", error)
      setMessage({ type: "error", text: "Failed to load backups: " + error.message })
    } finally {
      setBackupsLoading(false)
    }
  }

  const restoreBackup = async (lang, filename) => {
    if (!window.confirm(`Restore ${lang.toUpperCase()} translations from this backup?\n\nThis will replace the current ${lang.toUpperCase()} translations. A backup of the current state will be created automatically.`)) {
      return
    }

    setRestoringBackup(`${lang}-${filename}`)
    setMessage({ type: "loading", text: `Restoring ${lang.toUpperCase()} backup...` })

    try {
      const response = await fetch("https://www.casadipinta.com/backup-api.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lang, filename }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Restore failed")
      }

      setMessage({ type: "success", text: `${lang.toUpperCase()} restored successfully! Reloading...` })
      await loadBackups()
      setTimeout(() => window.location.reload(), 1500)
    } catch (error) {
      console.error("Restore error:", error)
      setMessage({ type: "error", text: "Restore failed: " + error.message })
    } finally {
      setRestoringBackup(null)
    }
  }

  const restoreBothBackups = async (itFilename, enFilename) => {
    if (!window.confirm("Restore BOTH Italian and English translations from these backups?\n\nThis will replace current translations for both languages. Backups of the current state will be created automatically.")) {
      return
    }

    setRestoringBackup("both")
    setMessage({ type: "loading", text: "Restoring both languages..." })

    try {
      const [itRes, enRes] = await Promise.all([
        fetch("https://www.casadipinta.com/backup-api.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lang: "it", filename: itFilename }),
        }),
        fetch("https://www.casadipinta.com/backup-api.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lang: "en", filename: enFilename }),
        }),
      ])

      const itData = await itRes.json()
      const enData = await enRes.json()

      if (!itRes.ok) throw new Error("IT: " + (itData.error || "failed"))
      if (!enRes.ok) throw new Error("EN: " + (enData.error || "failed"))

      setMessage({ type: "success", text: "Both languages restored! Reloading..." })
      setTimeout(() => window.location.reload(), 1500)
    } catch (error) {
      console.error("Restore error:", error)
      setMessage({ type: "error", text: "Restore failed: " + error.message })
    } finally {
      setRestoringBackup(null)
    }
  }

  // --- RENDERING FUNCTIONS ---

  // Helper to determine if a field should use textarea
  const shouldUseTextarea = (value) => {
    return (
      typeof value === "string" && (value.length > 80 || value.includes("\n"))
    )
  }

  // Render text input or textarea based on content
  const renderInput = (value, onChange, placeholder = "") => {
    if (shouldUseTextarea(value)) {
      return (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 font-serif"
          rows={Math.min(5, (value?.split("\n").length || 0) + 2)}
          placeholder={placeholder}
        />
      )
    }

    return (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 font-serif"
        placeholder={placeholder}
      />
    )
  }

  // For regular pages, render keys based on active section
  const renderRegularEditor = () => {
    if (!activeSection || !sections.length) return null

    // Get keys for active section
    const activeKeys =
      sections.find((s) => s.name === activeSection)?.keys || []

    return (
      <div className="space-y-4">
        {activeKeys.length === 0 ? (
          <p className="text-gray-500 italic">
            No translations in this section
          </p>
        ) : (
          activeKeys.map((key) => (
            <div key={key} className="border rounded-md p-3 bg-white shadow-sm">
              <div className="font-mono text-xs text-gray-500 mb-2 pb-1 border-b truncate">
                {key}
              </div>

              <div className="mb-2">
                <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                  {i18n.language.toUpperCase()}
                </div>
                {renderInput(flatTranslations[key], (value) =>
                  handleRegularChange(key, value)
                )}
              </div>

              <button
                onClick={() => toggleOtherKey(key)}
                className="text-blue-600 text-xs font-bold hover:underline focus:outline-none"
              >
                {toggleOtherKeys[key]
                  ? `Hide ${getSecondaryLanguage().toUpperCase()} translation`
                  : `Show ${getSecondaryLanguage().toUpperCase()} translation`}
              </button>

              {toggleOtherKeys[key] && (
                <div className="mt-3 pt-2 border-t">
                  <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                    {getSecondaryLanguage().toUpperCase()}
                  </div>
                  {renderInput(otherFlatTranslations[key], (value) =>
                    handleOtherRegularChange(key, value)
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    )
  }

  // Render a single menu item
  const renderMenuItem = (section, itemKey, item, index) => {
    const isCollapsed = collapsedItems[`${section}-${itemKey}`]
    const isOtherVisible = menuItemToggleOther[`${section}-${itemKey}`]

    return (
      <Draggable
        key={`${section}-${itemKey}`}
        draggableId={`${section}-${itemKey}`}
        index={index}
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="p-4 bg-white rounded-lg relative border border-gray-200 shadow-sm my-2 w-full max-w-full overflow-hidden"
          >
            <div className="flex flex-wrap justify-between items-center w-full">
              <h3
                className="font-medium cursor-pointer flex items-center truncate max-w-[60%] sm:max-w-[200px] mb-2 mr-2"
                onClick={() => toggleItemCollapse(section, itemKey)}
              >
                <span className="text-gray-400 mr-2 flex-shrink-0">::</span>
                <span className="truncate">{item.name}</span>
                <span className="ml-2 text-xs text-gray-500 flex-shrink-0">
                  {isCollapsed ? "▶" : "▼"}
                </span>
              </h3>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={() => toggleMenuItemOther(section, itemKey)}
                  className="text-blue-500 hover:text-blue-700 text-xs"
                >
                  {isOtherVisible
                    ? `Hide ${getSecondaryLanguage().toUpperCase()}`
                    : `Show ${getSecondaryLanguage().toUpperCase()}`}
                </button>
                <button
                  onClick={() => handleRemoveItem(section, itemKey)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Remove item"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
            </div>

            {!isCollapsed && (
              <div className="mt-4 space-y-4">
                <div className="text-xs text-gray-500 mb-2">Key: {itemKey}</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  {renderInput(item.name, (value) =>
                    handleMenuItemChange(section, itemKey, "name", value)
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  {renderInput(item.desc, (value) =>
                    handleMenuItemChange(section, itemKey, "desc", value)
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  {renderInput(item.price, (value) =>
                    handleMenuItemChange(section, itemKey, "price", value)
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allergens
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {Object.entries(allergens).map(([id, name]) => (
                      <label
                        key={id}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={
                            item.allergens?.includes(Number(id)) || false
                          }
                          onChange={() =>
                            handleAllergenToggle(section, itemKey, id)
                          }
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-offset-0 focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span>{name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {isOtherVisible && (
                  <div className="mt-6 p-4 border-t border-dashed border-gray-300 bg-gray-50 rounded-md">
                    <h4 className="font-medium mb-3 text-gray-700">
                      {getSecondaryLanguage().toUpperCase()} Version
                    </h4>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        {renderInput(
                          otherTranslations[getCurrentPage()]?.sections?.[
                            section
                          ]?.items?.[itemKey]?.name || "",
                          (value) =>
                            handleMenuItemOtherChange(
                              section,
                              itemKey,
                              "name",
                              value
                            )
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        {renderInput(
                          otherTranslations[getCurrentPage()]?.sections?.[
                            section
                          ]?.items?.[itemKey]?.desc || "",
                          (value) =>
                            handleMenuItemOtherChange(
                              section,
                              itemKey,
                              "desc",
                              value
                            )
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price
                        </label>
                        {renderInput(
                          otherTranslations[getCurrentPage()]?.sections?.[
                            section
                          ]?.items?.[itemKey]?.price || "",
                          (value) =>
                            handleMenuItemOtherChange(
                              section,
                              itemKey,
                              "price",
                              value
                            )
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Allergens
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {Object.entries(allergens).map(([id, name]) => (
                            <label
                              key={id}
                              className="flex items-center space-x-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  otherTranslations[
                                    getCurrentPage()
                                  ]?.sections?.[section]?.items?.[
                                    itemKey
                                  ]?.allergens?.includes(Number(id)) || false
                                }
                                onChange={() =>
                                  handleOtherAllergenToggle(
                                    section,
                                    itemKey,
                                    id
                                  )
                                }
                                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-offset-0 focus:ring-blue-200 focus:ring-opacity-50"
                              />
                              <span>{name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Draggable>
    )
  }

  // Render menu editor for menu pages
  const renderMenuEditor = () => {
    const currentPage = getCurrentPage()
    const menuSections = translations[currentPage]?.sections || {}

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-4">
          {/* Add Section button */}
          <div className="mb-6">
            <button
              onClick={handleAddSection}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
            >
              <span className="mr-2">+</span> Add New Section
            </button>
          </div>

          {/* Menu sections with drag and drop */}
          <Droppable droppableId="sections" type="section">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-4 w-full"
              >
                {Object.keys(menuSections).length === 0 ? (
                  <p className="text-gray-500 italic">
                    No menu sections yet. Add your first section!
                  </p>
                ) : (
                  Object.entries(menuSections).map(
                    ([sectionKey, sectionData], index) => (
                      <Draggable
                        key={sectionKey}
                        draggableId={`section-${sectionKey}`}
                        index={index}
                      >
                        {(providedDrag) => (
                          <div
                            ref={providedDrag.innerRef}
                            {...providedDrag.draggableProps}
                            className="border-b pb-4 mb-4 w-full"
                          >
                            {/* Section Header - Responsive layout */}
                            <div className="flex flex-col sm:flex-row sm:justify-between w-full">
                              <div className="flex items-center mb-2 sm:mb-0 w-full">
                                <div className="flex items-center">
                                  {/* Collapse toggle + drag handle combined for mobile */}
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() =>
                                        toggleSectionCollapse(sectionKey)
                                      }
                                      className="text-gray-400 focus:outline-none p-1"
                                      aria-label={
                                        collapsedSections[sectionKey]
                                          ? "Expand section"
                                          : "Collapse section"
                                      }
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        viewBox="0 0 16 16"
                                        className="transition-transform duration-200"
                                        style={{
                                          transform: collapsedSections[
                                            sectionKey
                                          ]
                                            ? "rotate(-90deg)"
                                            : "rotate(0)",
                                        }}
                                      >
                                        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                                      </svg>
                                    </button>

                                    <div
                                      {...providedDrag.dragHandleProps}
                                      className="text-gray-400 focus:outline-none cursor-move p-1"
                                      title="Drag to reorder section"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        viewBox="0 0 16 16"
                                      >
                                        <path d="M7 2a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                      </svg>
                                    </div>
                                  </div>

                                  {/* Section Titles */}
                                  <div className="flex-grow ml-2">
                                    <input
                                      type="text"
                                      value={sectionData.title || sectionKey}
                                      onChange={(e) => {
                                        setTranslations((prev) => ({
                                          ...prev,
                                          [currentPage]: {
                                            ...prev[currentPage],
                                            sections: {
                                              ...prev[currentPage].sections,
                                              [sectionKey]: {
                                                ...prev[currentPage].sections[
                                                  sectionKey
                                                ],
                                                title: e.target.value,
                                              },
                                            },
                                          },
                                        }))
                                      }}
                                      className="text-lg sm:text-xl font-bold border-b border-gray-300 bg-transparent focus:outline-none focus:border-blue-500 w-full"
                                    />

                                    <button
                                      onClick={() =>
                                        setSectionToggleOther((prev) => ({
                                          ...prev,
                                          [sectionKey]: !prev[sectionKey],
                                        }))
                                      }
                                      className="text-xs text-blue-500 mt-1 text-left"
                                    >
                                      {sectionToggleOther[sectionKey]
                                        ? `Hide ${getSecondaryLanguage().toUpperCase()}`
                                        : `Show ${getSecondaryLanguage().toUpperCase()}`}
                                    </button>

                                    {sectionToggleOther[sectionKey] && (
                                      <input
                                        type="text"
                                        value={
                                          otherTranslations[currentPage]
                                            ?.sections?.[sectionKey]?.title ||
                                          ""
                                        }
                                        onChange={(e) => {
                                          setOtherTranslations((prev) => ({
                                            ...prev,
                                            [currentPage]: {
                                              ...(prev[currentPage] || {}),
                                              sections: {
                                                ...((prev[currentPage] &&
                                                  prev[currentPage].sections) ||
                                                  {}),
                                                [sectionKey]: {
                                                  ...((prev[currentPage] &&
                                                    prev[currentPage]
                                                      .sections &&
                                                    prev[currentPage].sections[
                                                      sectionKey
                                                    ]) ||
                                                    {}),
                                                  title: e.target.value,
                                                },
                                              },
                                            },
                                          }))
                                        }}
                                        className="text-lg sm:text-xl font-bold border-b border-gray-300 bg-transparent focus:outline-none focus:border-blue-500 w-full mt-2"
                                        placeholder={`${getSecondaryLanguage().toUpperCase()} Title`}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons - Now in a responsive row that wraps when needed */}
                              <div className="flex mt-2 sm:mt-0 justify-end">
                                <button
                                  onClick={() => handleAddItem(sectionKey)}
                                  className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 text-sm flex items-center mr-2"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="12"
                                    height="12"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                    className="mr-1"
                                  >
                                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                                  </svg>
                                  <span className="whitespace-nowrap">
                                    Add Item
                                  </span>
                                </button>

                                <button
                                  onClick={() =>
                                    handleRemoveSection(sectionKey)
                                  }
                                  className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                                  title="Remove section"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                                  </svg>
                                </button>
                              </div>
                            </div>

                            {/* Section Items - Not collapsed */}
                            {!collapsedSections[sectionKey] && (
                              <Droppable droppableId={sectionKey} type="item">
                                {(providedDrop) => (
                                  <div
                                    ref={providedDrop.innerRef}
                                    {...providedDrop.droppableProps}
                                    className="space-y-2 pl-3 sm:pl-4 border-l border-gray-200 mt-3"
                                  >
                                    {Object.entries(sectionData.items || {})
                                      .length === 0 ? (
                                      <p className="text-gray-400 italic text-sm py-2">
                                        No items in this section. Add your first
                                        item!
                                      </p>
                                    ) : (
                                      Object.entries(
                                        sectionData.items || {}
                                      ).map(([itemKey, item], index) =>
                                        renderMenuItem(
                                          sectionKey,
                                          itemKey,
                                          item,
                                          index
                                        )
                                      )
                                    )}
                                    {providedDrop.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            )}
                          </div>
                        )}
                      </Draggable>
                    )
                  )
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    )
  }

  const renderConfirmDeleteModal = () => {
    if (!confirmDelete) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
          <h3 className="text-xl font-bold mb-4">Confirm Delete</h3>
          <p className="mb-4">
            Do you want to delete this item from the current language only or
            from both languages?
          </p>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setConfirmDelete(null)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={() => confirmDeleteAction(false)}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Current Language Only
            </button>
            <button
              onClick={() => confirmDeleteAction(true)}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Both Languages
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderConfirmDeleteSectionModal = () => {
    if (!confirmDeleteSection) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
          <h3 className="text-xl font-bold mb-4">Confirm Delete Section</h3>
          <p className="mb-4">
            Do you want to delete this entire section from the current language
            only or from both languages?
            <br />
            <br />
            <span className="text-red-500 font-bold">Warning:</span> This will
            delete all items in this section!
          </p>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setConfirmDeleteSection(null)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={() => confirmDeleteSectionAction(false)}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Current Language Only
            </button>
            <button
              onClick={() => confirmDeleteSectionAction(true)}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Both Languages
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderConfirmMissingModal = () => {
    if (!confirmMissing) return null

    const currentPage = getCurrentPage()
    const isMenuPage = ["menuCarta", "wineList", "menuAperitivo"].includes(
      currentPage
    )

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
          <h3 className="text-xl font-bold mb-4">Attention</h3>

          {isMenuPage ? (
            <>
              <p className="mb-2">
                You've edited the following fields in{" "}
                {i18n.language.toUpperCase()}, but you haven't updated the{" "}
                {getSecondaryLanguage().toUpperCase()} version:
              </p>
              <div className="mb-4 max-h-60 overflow-y-auto bg-gray-50 p-3 rounded border">
                <ul className="list-disc list-inside">
                  {confirmMissing.edited.map((key) => (
                    <li key={key} className="truncate text-sm">
                      {key}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <>
              <p className="mb-2">
                You've edited {confirmMissing.edited.length} fields in{" "}
                {i18n.language.toUpperCase()}, but{" "}
                {confirmMissing.missing.length} of them don't have corresponding
                {getSecondaryLanguage().toUpperCase()} translations:
              </p>
              <div className="mb-4 max-h-60 overflow-y-auto bg-gray-50 p-3 rounded border">
                <ul className="list-disc list-inside">
                  {confirmMissing.missing.map((key) => (
                    <li key={key} className="truncate text-sm">
                      {key}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          <p className="mb-4">
            Do you want to continue saving without updating these translations?
          </p>

          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setConfirmMissing(null)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Anyway
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Render status message
  const renderMessage = () => {
    if (!message) return null

    const bgColor =
      message.type === "success"
        ? "bg-green-100 border-green-500 text-green-800"
        : message.type === "error"
        ? "bg-red-100 border-red-500 text-red-800"
        : message.type === "loading"
        ? "bg-blue-100 border-blue-500 text-blue-800"
        : "bg-gray-100 border-gray-500 text-gray-800"

    return (
      <div
        className={`fixed bottom-4 right-4 p-4 rounded-md border ${bgColor} shadow-lg max-w-sm z-50`}
      >
        <div className="flex items-center">
          {message.type === "loading" && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-800 mr-2"></div>
          )}
          <p>{message.text}</p>
        </div>
      </div>
    )
  }

  const renderCombinedEditor = () => {
    const currentPage = getCurrentPage()
    return (
      <div className="space-y-8">
        {/* First render menu editor */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 border-b pb-2">Menu Items</h2>
          {renderMenuEditor()}
        </div>

        {/* Then render any regular strings for this page */}
        <div>
          <h2 className="text-xl font-bold mb-4 border-b pb-2">Page Text</h2>
          <div className="space-y-4">
            {Object.keys(flatTranslations).filter(
              (key) =>
                key.startsWith(`${currentPage}.`) && !key.includes(".sections.")
            ).length === 0 ? (
              <p className="text-gray-500 italic">
                No additional text for this page
              </p>
            ) : (
              Object.keys(flatTranslations)
                .filter(
                  (key) =>
                    key.startsWith(`${currentPage}.`) &&
                    !key.includes(".sections.")
                )
                .map((key) => (
                  <div
                    key={key}
                    className="border rounded-md p-3 bg-white shadow-sm"
                  >
                    <div className="font-mono text-xs text-gray-500 mb-2 pb-1 border-b truncate">
                      {key}
                    </div>

                    <div className="mb-2">
                      <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                        {i18n.language.toUpperCase()}
                      </div>
                      {renderInput(flatTranslations[key], (value) =>
                        handleRegularChange(key, value)
                      )}
                    </div>

                    <button
                      onClick={() => toggleOtherKey(key)}
                      className="text-blue-600 text-xs font-bold hover:underline focus:outline-none"
                    >
                      {toggleOtherKeys[key]
                        ? `Hide ${getSecondaryLanguage().toUpperCase()} translation`
                        : `Show ${getSecondaryLanguage().toUpperCase()} translation`}
                    </button>

                    {toggleOtherKeys[key] && (
                      <div className="mt-3 pt-2 border-t">
                        <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                          {getSecondaryLanguage().toUpperCase()}
                        </div>
                        {renderInput(otherFlatTranslations[key], (value) =>
                          handleOtherRegularChange(key, value)
                        )}
                      </div>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderRegularEditorWithPopup = () => {
    const currentPage = getCurrentPage()

    return (
      <div className="space-y-8">
        {/* Home Popup Section */}
        {currentPage === "home" && (
          <div className="border-b pb-6 mb-6">
            <h3 className="text-xl font-bold mb-4">Home Popup</h3>
            <div className="space-y-4 pl-4">
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    checked={flatTranslations["home.popup.enabled"] === "true"}
                    onChange={(e) => {
                      const value = e.target.checked ? "true" : "false"
                      handleRegularChange("home.popup.enabled", value)
                      handleOtherRegularChange("home.popup.enabled", value)
                    }}
                  />
                  <span className="ml-2">Enable Home Popup</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Popup Title ({i18n.language.toUpperCase()})
                </label>
                {renderInput(
                  flatTranslations["home.popup.title"] || "",
                  (value) => handleRegularChange("home.popup.title", value)
                )}

                <button
                  onClick={() =>
                    setToggleOtherKeys((prev) => ({
                      ...prev,
                      "home.popup.title": !prev["home.popup.title"],
                    }))
                  }
                  className="text-blue-600 text-xs font-bold hover:underline focus:outline-none mt-1"
                >
                  {toggleOtherKeys["home.popup.title"]
                    ? `Hide ${getSecondaryLanguage().toUpperCase()} translation`
                    : `Show ${getSecondaryLanguage().toUpperCase()} translation`}
                </button>

                {toggleOtherKeys["home.popup.title"] && (
                  <div className="mt-3 pt-2 border-t">
                    <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                      {getSecondaryLanguage().toUpperCase()}
                    </div>
                    {renderInput(
                      otherFlatTranslations["home.popup.title"] || "",
                      (value) =>
                        handleOtherRegularChange("home.popup.title", value)
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Popup Description ({i18n.language.toUpperCase()})
                </label>
                {renderInput(
                  flatTranslations["home.popup.description"] || "",
                  (value) =>
                    handleRegularChange("home.popup.description", value)
                )}

                <button
                  onClick={() =>
                    setToggleOtherKeys((prev) => ({
                      ...prev,
                      "home.popup.description": !prev["home.popup.description"],
                    }))
                  }
                  className="text-blue-600 text-xs font-bold hover:underline focus:outline-none mt-1"
                >
                  {toggleOtherKeys["home.popup.description"]
                    ? `Hide ${getSecondaryLanguage().toUpperCase()} translation`
                    : `Show ${getSecondaryLanguage().toUpperCase()} translation`}
                </button>

                {toggleOtherKeys["home.popup.description"] && (
                  <div className="mt-3 pt-2 border-t">
                    <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                      {getSecondaryLanguage().toUpperCase()}
                    </div>
                    {renderInput(
                      otherFlatTranslations["home.popup.description"] || "",
                      (value) =>
                        handleOtherRegularChange(
                          "home.popup.description",
                          value
                        )
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Text ({i18n.language.toUpperCase()})
                </label>
                {renderInput(
                  flatTranslations["home.popup.textLink"] || "",
                  (value) => handleRegularChange("home.popup.textLink", value)
                )}

                <button
                  onClick={() =>
                    setToggleOtherKeys((prev) => ({
                      ...prev,
                      "home.popup.textLink": !prev["home.popup.textLink"],
                    }))
                  }
                  className="text-blue-600 text-xs font-bold hover:underline focus:outline-none mt-1"
                >
                  {toggleOtherKeys["home.popup.textLink"]
                    ? `Hide ${getSecondaryLanguage().toUpperCase()} translation`
                    : `Show ${getSecondaryLanguage().toUpperCase()} translation`}
                </button>

                {toggleOtherKeys["home.popup.textLink"] && (
                  <div className="mt-3 pt-2 border-t">
                    <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                      {getSecondaryLanguage().toUpperCase()}
                    </div>
                    {renderInput(
                      otherFlatTranslations["home.popup.textLink"] || "",
                      (value) =>
                        handleOtherRegularChange("home.popup.textLink", value)
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link URL ({i18n.language.toUpperCase()})
                </label>
                {renderInput(
                  flatTranslations["home.popup.link"] || "",
                  (value) => handleRegularChange("home.popup.link", value)
                )}

                <button
                  onClick={() =>
                    setToggleOtherKeys((prev) => ({
                      ...prev,
                      "home.popup.link": !prev["home.popup.link"],
                    }))
                  }
                  className="text-blue-600 text-xs font-bold hover:underline focus:outline-none mt-1"
                >
                  {toggleOtherKeys["home.popup.link"]
                    ? `Hide ${getSecondaryLanguage().toUpperCase()} translation`
                    : `Show ${getSecondaryLanguage().toUpperCase()} translation`}
                </button>

                {toggleOtherKeys["home.popup.link"] && (
                  <div className="mt-3 pt-2 border-t">
                    <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                      {getSecondaryLanguage().toUpperCase()}
                    </div>
                    {renderInput(
                      otherFlatTranslations["home.popup.link"] || "",
                      (value) =>
                        handleOtherRegularChange("home.popup.link", value)
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Regular fields (excluding popup fields for home) */}
        <div>
          <h2 className="text-xl font-bold mb-4 border-b pb-2">
            {currentPage === "home" ? "Other Home Content" : "Page Content"}
          </h2>
          <div className="space-y-4">
            {Object.keys(flatTranslations).filter((key) => {
              const section = sections.find((s) => s.name === activeSection)
              if (!section) return false

              // For home page, exclude popup fields from regular section
              if (currentPage === "home" && key.startsWith("home.popup.")) {
                return false
              }

              return section.keys.includes(key)
            }).length === 0 ? (
              <p className="text-gray-500 italic">
                No additional content in this section
              </p>
            ) : (
              Object.keys(flatTranslations)
                .filter((key) => {
                  const section = sections.find((s) => s.name === activeSection)
                  if (!section) return false

                  // For home page, exclude popup fields from regular section
                  if (currentPage === "home" && key.startsWith("home.popup.")) {
                    return false
                  }

                  return section.keys.includes(key)
                })
                .map((key) => (
                  <div
                    key={key}
                    className="border rounded-md p-3 bg-white shadow-sm"
                  >
                    <div className="font-mono text-xs text-gray-500 mb-2 pb-1 border-b truncate">
                      {key}
                    </div>

                    <div className="mb-2">
                      <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                        {i18n.language.toUpperCase()}
                      </div>
                      {renderInput(flatTranslations[key], (value) =>
                        handleRegularChange(key, value)
                      )}
                    </div>

                    <button
                      onClick={() => toggleOtherKey(key)}
                      className="text-blue-600 text-xs font-bold hover:underline focus:outline-none"
                    >
                      {toggleOtherKeys[key]
                        ? `Hide ${getSecondaryLanguage().toUpperCase()} translation`
                        : `Show ${getSecondaryLanguage().toUpperCase()} translation`}
                    </button>

                    {toggleOtherKeys[key] && (
                      <div className="mt-3 pt-2 border-t">
                        <div className="text-xs uppercase text-gray-600 font-bold mb-1">
                          {getSecondaryLanguage().toUpperCase()}
                        </div>
                        {renderInput(otherFlatTranslations[key], (value) =>
                          handleOtherRegularChange(key, value)
                        )}
                      </div>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>
      </div>
    )
  }

  // Main render for entire editor
  if (!isEditMode) return null

  const currentPage = getCurrentPage()
  const isMenuPage = ["menuCarta", "wineList", "menuAperitivo"].includes(
    currentPage
  )

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-md px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          {/* Toggle sidebar button - visible only on mobile */}
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="mr-2 md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-md"
            aria-label="Toggle sections"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </button>

          {/* Title - hide on smallest screens */}
          <h2 className="hidden sm:block text-xl font-bold">
            Translation Editor
          </h2>

          {/* Language badge - always visible */}
          <span className="ml-2 text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded">
            {i18n.language.toUpperCase()}
          </span>

          {/* Page indicator - hide on smallest screens */}
          <span className="hidden sm:inline ml-2 text-sm text-gray-500">
            {isMenuPage ? `${currentPage}` : `${currentPage}`}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          {/* Save button - hide when special editors are shown (they have their own save) */}
          {!showReviewsManager && !showHoursEditor && !showEventsManager && !showSpecialMenuEditor && !showBackupManager && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              title="Save changes"
            >
              {isSaving ? (
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
              <span className="hidden sm:inline ml-1">Save</span>
            </button>
          )}

          {/* Close button - icon only on mobile */}
          <button
            onClick={closeEditor}
            className="bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600"
            title="Close editor"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span className="hidden sm:inline ml-1">Close</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - overlay on mobile */}
        {showSidebar && (
          <div className="fixed md:static md:w-64 w-3/4 bg-white shadow-md p-4 overflow-y-auto h-full z-10 transition-all duration-300 ease-in-out">
            {/* Close sidebar button - mobile only */}
            <button
              onClick={() => setShowSidebar(false)}
              className="md:hidden absolute top-2 right-2 text-gray-500 p-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Special Editors - at top for mobile accessibility */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h3 className="font-bold text-lg mb-3">⭐ Special Editors</h3>
              <button
                onClick={() => {
                  setShowReviewsManager(true)
                  setShowHoursEditor(false)
                  setShowEventsManager(false)
                  setShowSpecialMenuEditor(false)
                  setShowBackupManager(false)
                  setActiveSection(null)
                  if (window.innerWidth < 768) setShowSidebar(false)
                }}
                className={`w-full text-left px-3 py-2 rounded flex items-center gap-2 ${
                  showReviewsManager
                    ? "bg-green-500 text-white"
                    : "bg-green-50 hover:bg-green-100 border border-green-200"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                Reviews Manager
              </button>
              <button
                onClick={() => {
                  setShowHoursEditor(true)
                  setShowReviewsManager(false)
                  setShowEventsManager(false)
                  setShowSpecialMenuEditor(false)
                  setShowBackupManager(false)
                  setActiveSection(null)
                  if (window.innerWidth < 768) setShowSidebar(false)
                }}
                className={`w-full text-left px-3 py-2 rounded flex items-center gap-2 mt-2 ${
                  showHoursEditor
                    ? "bg-green-500 text-white"
                    : "bg-green-50 hover:bg-green-100 border border-green-200"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Hours Editor
              </button>
              <button
                onClick={() => {
                  setShowEventsManager(true)
                  setShowReviewsManager(false)
                  setShowHoursEditor(false)
                  setShowSpecialMenuEditor(false)
                  setShowBackupManager(false)
                  setActiveSection(null)
                  if (window.innerWidth < 768) setShowSidebar(false)
                }}
                className={`w-full text-left px-3 py-2 rounded flex items-center gap-2 mt-2 ${
                  showEventsManager
                    ? "bg-green-500 text-white"
                    : "bg-green-50 hover:bg-green-100 border border-green-200"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Events Manager
              </button>
              <button
                onClick={() => {
                  setShowSpecialMenuEditor(true)
                  setShowReviewsManager(false)
                  setShowHoursEditor(false)
                  setShowEventsManager(false)
                  setShowBackupManager(false)
                  setActiveSection(null)
                  if (window.innerWidth < 768) setShowSidebar(false)
                }}
                className={`w-full text-left px-3 py-2 rounded flex items-center gap-2 mt-2 ${
                  showSpecialMenuEditor
                    ? "bg-green-500 text-white"
                    : "bg-green-50 hover:bg-green-100 border border-green-200"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Special Menu
              </button>
              <button
                onClick={() => {
                  setShowBackupManager(true)
                  setShowReviewsManager(false)
                  setShowHoursEditor(false)
                  setShowEventsManager(false)
                  setShowSpecialMenuEditor(false)
                  setActiveSection(null)
                  loadBackups()
                  if (window.innerWidth < 768) setShowSidebar(false)
                }}
                className={`w-full text-left px-3 py-2 rounded flex items-center gap-2 mt-2 ${
                  showBackupManager
                    ? "bg-amber-500 text-white"
                    : "bg-amber-50 hover:bg-amber-100 border border-amber-200"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Backup & Recovery
              </button>
            </div>

            <h3 className="font-bold text-lg mb-3">Sections</h3>
            <ul className="space-y-1">
              {sections.map((section) => (
                <li key={section.name}>
                  <button
                    onClick={() => {
                      setShowReviewsManager(false)
                      setShowHoursEditor(false)
                      setShowEventsManager(false)
                      setShowSpecialMenuEditor(false)
                      setShowBackupManager(false)
                      handleSectionChange(section.name)
                      // Only close sidebar on mobile (< 768px)
                      if (window.innerWidth < 768) setShowSidebar(false)
                    }}
                    className={`w-full text-left px-3 py-2 rounded ${
                      activeSection === section.name && !showReviewsManager && !showHoursEditor && !showEventsManager && !showSpecialMenuEditor && !showBackupManager
                        ? "bg-blue-500 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {section.name}
                    <span className="ml-2 text-xs opacity-75">
                      ({section.keys.length})
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Main editor area - adjust for sidebar overlay on mobile */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <p className="ml-3 text-gray-600">Loading translations...</p>
            </div>
          ) : showReviewsManager ? (
            <ReviewsManager />
          ) : showHoursEditor ? (
            <HoursEditor />
          ) : showEventsManager ? (
            <EventsManager />
          ) : showSpecialMenuEditor ? (
            <SpecialMenuEditor />
          ) : showBackupManager ? (
            <div className="h-full overflow-y-auto" style={{ maxHeight: "calc(100vh - 100px)" }}>
              <div className="p-3 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-heading text-secondary-200">Backup & Recovery</h1>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      Backups are created automatically before every save. Restore any previous version.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={loadBackups}
                    disabled={backupsLoading}
                    className="w-full sm:w-auto px-4 py-2 rounded-lg font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                  >
                    {backupsLoading ? "Loading..." : "Refresh"}
                  </button>
                </div>

                {backupsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    <p className="ml-3 text-gray-500">Loading backups...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Quick restore: find matching timestamps */}
                    {(() => {
                      const itTimestamps = new Set(backups.it.map(b => b.timestamp))
                      const matchingPairs = backups.it.filter(b => backups.en.some(e => e.timestamp === b.timestamp))
                      if (matchingPairs.length > 0) {
                        return (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              Quick Restore (Both Languages)
                            </h3>
                            <p className="text-amber-700 text-sm mb-3">Restore both IT and EN from the same point in time:</p>
                            <div className="space-y-2">
                              {matchingPairs.slice(0, 10).map((itBackup) => {
                                const enBackup = backups.en.find(e => e.timestamp === itBackup.timestamp)
                                return (
                                  <div key={itBackup.timestamp} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100">
                                    <div>
                                      <span className="font-mono text-sm">{itBackup.readable}</span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        IT: {itBackup.sizeFormatted} / EN: {enBackup.sizeFormatted}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => restoreBothBackups(itBackup.filename, enBackup.filename)}
                                      disabled={!!restoringBackup}
                                      className="px-3 py-1 text-sm rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
                                    >
                                      {restoringBackup === "both" ? "Restoring..." : "Restore Both"}
                                    </button>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )
                      }
                      return null
                    })()}

                    {/* Individual language backups */}
                    {["it", "en"].map((lang) => (
                      <div key={lang} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className={`px-4 py-3 ${lang === "it" ? "bg-green-50 border-b border-green-200" : "bg-blue-50 border-b border-blue-200"}`}>
                          <h3 className="font-semibold flex items-center gap-2">
                            <span className={`w-6 h-5 rounded ${lang === "it" ? "bg-green-600" : "bg-blue-600"} text-white text-xs flex items-center justify-center font-bold`}>
                              {lang.toUpperCase()}
                            </span>
                            {lang === "it" ? "Italian" : "English"} Backups
                            <span className="text-sm font-normal text-gray-500">({backups[lang].length})</span>
                          </h3>
                        </div>
                        {backups[lang].length === 0 ? (
                          <div className="p-4 text-gray-500 text-sm">No backups available yet.</div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {backups[lang].map((backup, index) => (
                              <div key={backup.filename} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                                <div className="flex items-center gap-3">
                                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${index === 0 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                                    {index === 0 ? "latest" : `#${index + 1}`}
                                  </span>
                                  <div>
                                    <span className="font-mono text-sm">{backup.readable}</span>
                                    <span className="text-xs text-gray-400 ml-2">{backup.sizeFormatted}</span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => restoreBackup(lang, backup.filename)}
                                  disabled={!!restoringBackup}
                                  className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 ${
                                    lang === "it"
                                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                                      : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                  }`}
                                >
                                  {restoringBackup === `${lang}-${backup.filename}` ? "Restoring..." : "Restore"}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-700 mb-2">How it works:</h3>
                      <ul className="text-gray-600 text-sm space-y-1 list-disc list-inside">
                        <li>A backup is automatically created before every save (from any editor)</li>
                        <li>Up to 40 backups are kept per language (oldest are pruned automatically)</li>
                        <li>Restoring also creates a backup of the current state first (so restores are reversible)</li>
                        <li>"Quick Restore" restores both languages from the same point in time</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : isMenuPage ? (
            renderCombinedEditor()
          ) : activeSection === "home" ? (
            renderRegularEditorWithPopup()
          ) : (
            renderRegularEditor()
          )}
        </div>
      </div>

      {/* Modals - make mobile friendly */}
      {showAddItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded-lg w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Add New Item</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key (lowercase letters only, no spaces)
                </label>
                <input
                  type="text"
                  value={newItem.key}
                  onChange={(e) =>
                    setNewItem((prev) => ({ ...prev, key: e.target.value }))
                  }
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2"
                  placeholder="e.g. spaghetti"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name ({i18n.language.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) =>
                    setNewItem((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description ({i18n.language.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={newItem.desc}
                  onChange={(e) =>
                    setNewItem((prev) => ({ ...prev, desc: e.target.value }))
                  }
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price
                </label>
                <input
                  type="text"
                  value={newItem.price}
                  onChange={(e) =>
                    setNewItem((prev) => ({ ...prev, price: e.target.value }))
                  }
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allergens
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 border rounded-md">
                  {Object.entries(allergens).map(([id, name]) => (
                    <label
                      key={id}
                      className="flex items-center space-x-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={newItem.allergens.includes(Number(id))}
                        onChange={() =>
                          setNewItem((prev) => ({
                            ...prev,
                            allergens: prev.allergens.includes(Number(id))
                              ? prev.allergens.filter((a) => a !== Number(id))
                              : [...prev.allergens, Number(id)].sort(
                                  (a, b) => a - b
                                ),
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-offset-0 focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span>{name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setShowAddItem(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNewItem}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Other modals - updated with same mobile-friendly styling */}
      {showAddSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded-lg w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            {/* section modal content */}
            <h3 className="text-xl font-bold mb-4">Add New Section</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Key (lowercase letters only, no spaces)
                </label>
                <input
                  type="text"
                  value={newSection.key}
                  onChange={(e) =>
                    setNewSection((prev) => ({ ...prev, key: e.target.value }))
                  }
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2"
                  placeholder="e.g. starters"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Title ({i18n.language.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={newSection.title}
                  onChange={(e) =>
                    setNewSection((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2"
                  placeholder="e.g. Starters"
                />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setShowAddSection(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNewSection}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add Section
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && renderConfirmDeleteModal()}
      {confirmDeleteSection && renderConfirmDeleteSectionModal()}
      {confirmMissing && renderConfirmMissingModal()}
      {renderMessage()}
    </div>
  )
}

export default TranslationEditor
